import cn from 'classnames';
import { BaseRowData } from '@adv-frontend/data-provider';
import { ReactNode, useMemo } from 'react';
import { useHStack } from '@direct-frontend/components';
import { ColumnCellWrapperTemplateProps, GridCell } from '../../../types';
import styles from '../Grid.module.css';

// Тип виртуализированной ячейки
interface VirtualizedCell<RowData extends BaseRowData> extends GridCell<RowData> {
    offsetLeft?: number;
}

/**
 * Пропсы компонента ячейки LoadMoreRow.
 */
interface LoadMoreCellProps<RowData extends BaseRowData> {
    cell: VirtualizedCell<RowData>;
    children: ReactNode;
}

function defaultRenderCellWrapper({
    children,
    className,
    style,
    'data-testid': dataTestId,
}: ColumnCellWrapperTemplateProps) {
    return (
        <div className={className} style={style} data-testid={dataTestId}>
            {children}
        </div>
    );
}

/**
 * Компонент ячейки для строки "загрузить еще".
 */
export function LoadMoreCell<RowData extends BaseRowData>({ cell, children }: LoadMoreCellProps<RowData>) {
    const hasRightBorder = cell.column.columnDef.hasDelimiter;
    const width = cell.column.getSize();

    const inlineStyles = useMemo(() => {
        // Если столбец виртуализирован, то используем абсолютное позиционирование
        if (cell.offsetLeft !== undefined) {
            return {
                width,
                position: 'absolute' as const,
                left: cell.offsetLeft,
            };
        }

        return { width };
    }, [cell.offsetLeft, width]);
    const renderWrapper = cell.column.columnDef.renderCellWrapper ?? defaultRenderCellWrapper;
    const stackClasses = useHStack({});

    return renderWrapper({
        children: children,
        className: cn(stackClasses, styles.cell, hasRightBorder ? styles.rightBorder : undefined),
        style: inlineStyles,
        'data-testid': `Grid.Cell-${cell.id}`,
    });
}
LoadMoreCell.displayName = 'LoadMoreCell';
